import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Menu, MenuItem, TextField, Typography } from "@mui/material";

const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const authHeaders = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("jwt") || "";
  const raw = String(token).replace(/^Bearer\s+/i, "").trim();
  return raw ? { Authorization: `Bearer ${raw}`, "x-access-token": raw } : {};
};

export default function MentionableNoteField({ value, onChange, onMentionedUserIdsChange, placeholder, ...props }) {
  const fieldRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [mention, setMention] = useState(null);
  const currentUserId = useMemo(() => {
    for (const storage of [localStorage, sessionStorage]) {
      try {
        const user = JSON.parse(storage.getItem("user") || "null");
        const id = Number(user?.id ?? user?.user_id);
        if (Number.isFinite(id) && id > 0) return id;
      } catch {
        // Try the next storage location.
      }
    }
    return null;
  }, []);

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE}/api/users/recruiters?limit=300`, { headers: authHeaders(), credentials: "include" })
      .then((response) => response.ok ? response.json() : { users: [] })
      .then((payload) => { if (active) setUsers(Array.isArray(payload?.users) ? payload.users : []); })
      .catch(() => { if (active) setUsers([]); });
    return () => { active = false; };
  }, []);

  const matches = useMemo(() => {
    if (!mention) return [];
    const query = mention.query.toLowerCase();
    return users
      .filter((user) => Number(user?.id ?? user?.user_id) !== currentUserId)
      .filter((user) => String(user?.name || "").toLowerCase().includes(query))
      .slice(0, 8);
  }, [currentUserId, mention, users]);

  const handleChange = (event) => {
    const next = event.target.value;
    onChange(event);
    const beforeCursor = next.slice(0, event.target.selectionStart ?? next.length);
    // Mentions use a single searchable token. A space ends the mention so
    // users can continue writing a sentence without needing punctuation to
    // dismiss the suggestion menu. Full display names are still inserted when
    // a suggestion is chosen.
    const match = beforeCursor.match(/(^|\s)@([\w.-]*)$/);
    setMention(match ? { start: beforeCursor.lastIndexOf("@"), query: match[2] || "" } : null);
  };

  const chooseUser = (user) => {
    if (!mention) return;
    const current = String(value || "");
    const next = `${current.slice(0, mention.start)}@${user.name} ${current.slice(mention.start + mention.query.length + 1)}`;
    onChange({ target: { value: next } });
    onMentionedUserIdsChange?.((previous) => [...new Set([...(previous || []), Number(user.id)])]);
    setMention(null);
    fieldRef.current?.focus?.();
  };

  return <Box>
    <TextField {...props} inputRef={fieldRef} value={value} onChange={handleChange} placeholder={placeholder} />
    <Menu
      anchorEl={fieldRef.current}
      open={Boolean(mention && fieldRef.current)}
      onClose={() => setMention(null)}
      // Keep the cursor in the note field so a user can finish typing a name
      // after entering "@". MUI Menu otherwise focuses its first suggestion.
      autoFocus={false}
      disableAutoFocusItem
      MenuListProps={{ dense: true, autoFocusItem: false }}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      {matches.length ? matches.map((user) => <MenuItem key={user.id} onMouseDown={(event) => event.preventDefault()} onClick={() => chooseUser(user)}>
        <Box><Typography sx={{ fontSize: 14, fontWeight: 600 }}>{user.name}</Typography><Typography sx={{ fontSize: 12, color: "#667085" }}>{user.email}</Typography></Box>
      </MenuItem>) : <MenuItem disabled><Typography sx={{ fontSize: 13 }}>No matching people</Typography></MenuItem>}
    </Menu>
  </Box>;
}
